import { useCallback, useRef, useState } from 'react';

import { 
	FormValidationHook, 
	FormValidationInputEvent,
} from '@/types/FormValidationHook';

import {
	ERROR_PRICE_GENERAL, 
	ERROR_PRICE_MAX,
	ERROR_PRICE_MIN,
	ERROR_PRICE_MISSING,
	ERROR_PRICE_NUMERIC,
} from '@/constants/errors';

import { getCurrencyMinorCount } from '@/utils/currency';
import { 
	isJsEvent,
	safeParseFloat, 
} from '@/utils/general';
import { normalizePrice } from '@/utils/formatting';

// Interface for validation config options
interface ValidationConfig {
	allowEmpty?: boolean;
	hideError?: boolean;
	max?: number;
	min?: number;
	normalize?: boolean;
}

const useFormValidationPrice = (initialValue: string): FormValidationHook => {

	const initialRef = useRef(initialValue);
  
	const [value, setValue] = useState<string>(initialValue || '');
	const [error, setError] = useState<string>('');

	const validate = useCallback((test: string | FormValidationInputEvent | null = null, config: ValidationConfig = {}): string => {
	
		try {

			let respError = '';
			let testVal = test;

			if(isJsEvent(testVal)) {
				testVal = (test as FormValidationInputEvent).target.value;
			} else if(test === null) {
				testVal = value;
			}

			// Configs
			const allowEmpty = config.allowEmpty || false;
			const shouldNormalize = config.normalize || false;
			const showError = !(config.hideError || false);

			if(allowEmpty && testVal === '') {
				return '';
			}

			if(testVal === null || testVal === '' || !testVal) {
				respError = ERROR_PRICE_MISSING;
			} else if(typeof testVal === 'string' && testVal.trim().length === 0) {
				respError = ERROR_PRICE_MISSING;
			} 

			try {
				if(!respError) {

					const floatVal = safeParseFloat(testVal);

					const globalMin = 0;
					const globalMax = 1000000000 * getCurrencyMinorCount();  // 1B minor currency units

					const dynamicMin = config.min ? parseFloat(config.min.toString()) : globalMin;
					const dynamicMax = config.max ? parseFloat(config.max.toString()) : globalMax;

					if(floatVal < dynamicMin) {
						respError = ERROR_PRICE_MIN;
					} else if(floatVal > dynamicMax) {
						respError = ERROR_PRICE_MAX;
					}
				}
			} catch(numericErr) {
				respError = respError || ERROR_PRICE_NUMERIC;
			}

			if(showError || !respError) {
				setError(respError);
			}

			if(shouldNormalize) {
				setValue(normalizePrice(value));
			}

			return respError;

		} catch(err) {
			console.error(err);
			setError(ERROR_PRICE_GENERAL);
			return ERROR_PRICE_GENERAL;
		}
	}, [value]);

	const isValid = (test: string | null = null, config: Record<string, any> = {}) => {
		return validate(test, config) === '';
	};

	const handleChange = (evt: FormValidationInputEvent, config = {}) => {
		setValue(evt.target.value);
		if(error) {
			validate(evt.target.value, Object.assign({}, config, { hideError: false, normalize: false }));
		}
	};

	const changeValue = (val: string, config = {}) => {
		setValue(val);
		if(error) {
			validate(val, Object.assign({}, config, { hideError: true, normalize: false }));
		}
	};

	const reset = useCallback(() => {
		setValue(initialRef.current || '');
		setError('');
	}, []);

	return {
		value,
		error,
		validate,
		isValid,
		onChange: handleChange,
		setValue: changeValue,
		reset,
	};
};

export default useFormValidationPrice;
